import { DesignReportPayload } from 'components/ExportDesign/ExportDesignReportCard';
import { ClassificationSummary, ProductSpecsSnapshot } from 'components/ExportDesign/exportDesignProgress';

// The Design Assistant's chat, kept on the saved ticket. Modelled on the Export Workflow's
// (utils/exportChatTranscript.ts, "Bug 9"): the chat as it was on screen rides INSIDE
// `workflowData[0]` next to `_workflowStateJson`, because the Case schema has no field for it and
// Mongoose drops top-level extras. What the server already keeps (`state.conversation`: every user
// and assistant message, with its time) stays as the fallback for tickets saved before this.
//
// Stored as `{v: 1, messages}`. Thinking panels are never stored (large, and only meaningful live);
// everything else a message carried is: its cards, the files attached, the next-step suggestions.

export const DESIGN_CHAT_TRANSCRIPT_KEY = '_chatTranscriptJson';

// Keeps the ticket far below Mongo's 16 MB document cap and the server's 10 MB request limit, the
// same bound the Export Workflow uses. A real chat is tens of KB; this is for outliers.
export const DESIGN_CHAT_TRANSCRIPT_MAX_CHARS = 1_500_000;

const TRANSCRIPT_VERSION = 1;
const THINKING_PLACEHOLDER = 'Thinking...';

export interface DesignTranscriptNextStep {
  label: string;
  prompt: string;
}

/** One chat message as stored. A subset of the chat's own message type. */
export interface DesignTranscriptMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  /** ISO time the message was created (absent on messages from before this was recorded). */
  ts?: string;
  attachedFilenames?: string[];
  thoughtSeconds?: number;
  productSpecsCard?: ProductSpecsSnapshot;
  keySpecsCard?: ClassificationSummary;
  report?: DesignReportPayload;
  continuePrompt?: string;
  nextSteps?: DesignTranscriptNextStep[];
  disclaimer?: string;
}

/**
 * What the live chat hands in: a message that may also carry the (unstored) thinking panel, and may be
 * marked `ephemeral` — a message the chat drew itself from saved state (the "Reopened…" note, a report
 * card rebuilt from the state) rather than something said in the session. Ephemeral messages are not
 * saved: they are rebuilt on every restore, so saving them would stack another copy on each Continue.
 */
export type DesignChatMessageLike = DesignTranscriptMessage & {
  thoughtSteps?: unknown;
  ephemeral?: boolean;
};

const isString = (value: unknown): value is string => typeof value === 'string';

/** The messages worth keeping: real text, never the placeholder or a message the chat drew itself. */
const keepable = (messages: unknown): DesignChatMessageLike[] =>
  (Array.isArray(messages) ? messages : []).filter(
    (m): m is DesignChatMessageLike =>
      Boolean(m) &&
      isString(m.id) &&
      isString(m.text) &&
      m.text !== THINKING_PLACEHOLDER &&
      m.ephemeral !== true,
  );

// How much of a message's cards to keep, from everything to nothing. Each step down drops what is
// largest or re-derivable first: the report payload is rebuilt from the saved state
// (exportDesignResume.reportPayloadFromState), the small cards go last.
type Fidelity = 'full' | 'no-report-payload' | 'no-cards';
const FIDELITY_STEPS: Fidelity[] = ['full', 'no-report-payload', 'no-cards'];

const toStored = (message: DesignChatMessageLike, fidelity: Fidelity): DesignTranscriptMessage => {
  const stored: DesignTranscriptMessage = {
    id: message.id,
    sender: message.sender,
    text: message.text,
  };
  if (isString(message.ts)) stored.ts = message.ts;
  if (Array.isArray(message.attachedFilenames) && message.attachedFilenames.length) {
    stored.attachedFilenames = message.attachedFilenames.filter(isString);
  }
  if (typeof message.thoughtSeconds === 'number') stored.thoughtSeconds = message.thoughtSeconds;
  if (isString(message.continuePrompt) && message.continuePrompt) {
    stored.continuePrompt = message.continuePrompt;
  }
  if (Array.isArray(message.nextSteps) && message.nextSteps.length) stored.nextSteps = message.nextSteps;
  if (isString(message.disclaimer) && message.disclaimer) stored.disclaimer = message.disclaimer;
  if (fidelity !== 'no-cards') {
    if (message.productSpecsCard) stored.productSpecsCard = message.productSpecsCard;
    if (message.keySpecsCard) stored.keySpecsCard = message.keySpecsCard;
  }
  if (fidelity === 'full' && message.report) stored.report = message.report;
  return stored;
};

/**
 * The chat to store on the ticket, as JSON — '' when there is nothing worth storing (no message from
 * the user) or it cannot fit even without its cards, in which case the ticket still has the server's
 * own record of the conversation.
 */
export const serializeDesignChatTranscript = (messages: unknown): string => {
  const kept = keepable(messages);
  if (!kept.some((m) => m.sender === 'user')) return '';
  for (const fidelity of FIDELITY_STEPS) {
    try {
      const json = JSON.stringify({
        v: TRANSCRIPT_VERSION,
        messages: kept.map((m) => toStored(m, fidelity)),
      });
      if (json.length <= DESIGN_CHAT_TRANSCRIPT_MAX_CHARS) return json;
    } catch (_error) {
      return '';
    }
  }
  return '';
};

const isNextStep = (value: any): value is DesignTranscriptNextStep =>
  Boolean(value) && isString(value.label) && isString(value.prompt);

/** One stored message, checked field by field: a saved ticket is data, not trusted. */
const readStoredMessage = (raw: any): DesignTranscriptMessage | null => {
  if (!raw || !isString(raw.id) || !isString(raw.text)) return null;
  if (raw.sender !== 'user' && raw.sender !== 'bot') return null;
  const message: DesignTranscriptMessage = { id: raw.id, sender: raw.sender, text: raw.text };
  if (isString(raw.ts)) message.ts = raw.ts;
  if (Array.isArray(raw.attachedFilenames)) {
    const names = raw.attachedFilenames.filter(isString);
    if (names.length) message.attachedFilenames = names;
  }
  if (typeof raw.thoughtSeconds === 'number') message.thoughtSeconds = raw.thoughtSeconds;
  if (isString(raw.continuePrompt) && raw.continuePrompt) message.continuePrompt = raw.continuePrompt;
  if (Array.isArray(raw.nextSteps)) {
    const steps = raw.nextSteps.filter(isNextStep);
    if (steps.length) message.nextSteps = steps;
  }
  if (isString(raw.disclaimer) && raw.disclaimer) message.disclaimer = raw.disclaimer;
  if (raw.productSpecsCard && typeof raw.productSpecsCard === 'object') {
    message.productSpecsCard = raw.productSpecsCard;
  }
  if (raw.keySpecsCard && typeof raw.keySpecsCard === 'object') message.keySpecsCard = raw.keySpecsCard;
  if (raw.report && typeof raw.report === 'object') message.report = raw.report;
  return message;
};

/** The chat stored on a saved ticket ([] when absent, unreadable, or from a version we don't know). */
export const savedDesignChatTranscript = (savedCase: Record<string, any>): DesignTranscriptMessage[] => {
  const workflowData = savedCase?.workflowData;
  const snapshot = Array.isArray(workflowData) ? workflowData[0] : workflowData;
  const raw = snapshot?.[DESIGN_CHAT_TRANSCRIPT_KEY];
  if (!isString(raw) || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== TRANSCRIPT_VERSION || !Array.isArray(parsed.messages)) return [];
    const messages = parsed.messages
      .map(readStoredMessage)
      .filter((m: DesignTranscriptMessage | null): m is DesignTranscriptMessage => m !== null);
    return messages.some((m: DesignTranscriptMessage) => m.sender === 'user') ? messages : [];
  } catch (_error) {
    return [];
  }
};

/**
 * What a save of this chat would capture, as a string: the content that would actually be stored for
 * each kept message (its text, time, files, cards and suggestions), so any change to what a save would
 * write reads as unsaved work. Counting messages and characters stood in for that before, and missed an
 * edit that kept the length, or a change to a card or a file. The unsaved-work check compares it with the
 * one taken at the last save. A payload that cannot be serialized falls back to the text alone.
 */
export const chatSignature = (messages: unknown): string => {
  const kept = keepable(messages);
  try {
    return JSON.stringify(kept.map((message) => toStored(message, 'full')));
  } catch (_error) {
    return JSON.stringify(
      kept.map((message) => ({ id: message.id, sender: message.sender, text: message.text })),
    );
  }
};

// Every report turn the server stores starts with this heading (export_design_workflow._present_report),
// which is a steadier marker than comparing the summary's wording: that text is normalised on the way
// into the conversation log and can differ slightly from the stored report.
const REPORT_HEADING = '# final design summary';

/**
 * The turn that delivered the latest Final Design Summary, for a chat stored as plain turns
 * (`state.conversation`) where nothing marks which one it was; -1 when none did. The chat shows a report
 * as a card in place of its text, so knowing the turn stops the same summary appearing twice.
 */
export const findReportTurnIndex = (
  turns: Array<{ role?: string; sender?: string; text?: string }>,
): number => {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const turn = turns[i];
    const isAssistant = turn?.role === 'assistant' || turn?.sender === 'bot';
    if (isAssistant && isString(turn.text) && turn.text.trim().toLowerCase().startsWith(REPORT_HEADING)) {
      return i;
    }
  }
  return -1;
};
